import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ExternalLink } from "lucide-react";
import {
  roundMoney,
  toMoneyNumber,
  computeLineWithVatAmounts,
  documentNotesIndicateNoVat,
} from "@/lib/superfaktura-document-amounts";

interface DocumentDetailsProps {
  document: {
    id: string;
    document_type: string;
    document_number?: string;
    document_name?: string;
    total_amount?: number;
    currency?: string;
    document_date?: string;
    due_date?: string;
    status?: string;
    pdf_url?: string;
    raw_data?: any;
    extracted_data?: any;
    document_items?: Array<{
      id: string;
      item_name: string;
      quantity: number;
      unit_price: number;
      total_price: number;
      unit?: string;
      description?: string;
    }>;
    // Customer data
    customer_name?: string;
    customer_email?: string;
    customer_address?: string;
    customer_city?: string;
    customer_postal_code?: string;
    customer_country?: string | { name?: string; iso?: string };
    customer_ico?: string;
    customer_dic?: string;
    customer_ic_dph?: string;
    // Tax information
    tax_amount?: number;
    subtotal_amount?: number;
    tax_rate?: number;
    // Metadata fields
    valid_until?: string;
    payment_method?: string;
    delivery_date?: string;
    related_document_number?: string;
    related_document_type?: string;
    variable_symbol?: string;
    notes?: string;
  };
  isOpen: boolean;
  onClose: () => void;
  dealId?: string;
}

const documentTypeLabels: Record<string, string> = {
  quote: "Árajánlat",
  invoice: "Számla",
  deposit_invoice: "Előlegszámla",
  proforma: "Proforma",
  credit_note: "Jóváírás",
  expense: "Kiadás",
};

export function DocumentDetails({
  document,
  isOpen,
  onClose,
  dealId,
}: DocumentDetailsProps) {
  const { t } = useTranslation();
  const typeLabel = documentTypeLabels[document.document_type] || document.document_type;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    try {
      return new Date(dateStr).toLocaleDateString("hu-HU");
    } catch {
      return dateStr;
    }
  };

  const formatAmount = (amount?: number | string, currency?: string) => {
    if (amount === undefined || amount === null) return "—";
    const n = toMoneyNumber(amount);
    if (!Number.isFinite(n)) return "—";
    const curr = currency || "EUR";
    return `${n.toFixed(2)} ${curr}`;
  };

  /** Bruttó egységár: množstvo > 1 → viac desatinných (súlad so Spolu s DPH = round(net×množstvo×(1+DPH),2)). */
  const formatAmountUnitWithVat = (
    amount?: number | string,
    currency?: string,
    lineQuantity?: number
  ) => {
    if (amount === undefined || amount === null) return "—";
    const n = toMoneyNumber(amount);
    if (!Number.isFinite(n)) return "—";
    const curr = currency || "EUR";
    const decimals = lineQuantity != null && lineQuantity !== 1 ? 4 : 2;
    return `${n.toFixed(decimals)} ${curr}`;
  };

  // Get items from document_items, extracted_data, or raw_data
  // Priority: document_items > extracted_data.items > raw_data
  // Use Set to avoid duplicates based on item name + quantity + unit_price
  const itemsMap = new Map<string, any>();
  
  // First, try document_items (from database)
  if (document.document_items && Array.isArray(document.document_items) && document.document_items.length > 0) {
    document.document_items.forEach((item: any, idx: number) => {
      const unit = toMoneyNumber(item.unit_price);
      const tot = toMoneyNumber(item.total_price);
      const qty = toMoneyNumber(item.quantity);
      const key = `${item.item_name || item.name || ''}_${qty}_${unit}`;
      if (!itemsMap.has(key)) {
        itemsMap.set(key, {
          id: item.id || `item-${idx}`,
          item_name: item.item_name || item.name || '',
          quantity: qty,
          unit_price: unit,
          total_price: tot,
          unit: item.unit || 'ks',
          description: item.description || '',
          taxRate: toMoneyNumber(item.taxRate ?? item.raw_data?.taxRate ?? document.tax_rate ?? 0),
        });
      }
    });
  }
  
  // If no items from document_items, try extracted_data.items
  if (itemsMap.size === 0 && document.extracted_data?.items && Array.isArray(document.extracted_data.items)) {
    document.extracted_data.items.forEach((item: any, idx: number) => {
      const unit = toMoneyNumber(item.unitPrice);
      const tot = toMoneyNumber(item.totalPrice);
      const qty = toMoneyNumber(item.quantity);
      const key = `${item.name || ''}_${qty}_${unit}`;
      if (!itemsMap.has(key)) {
        itemsMap.set(key, {
          id: `item-${idx}`,
          item_name: item.name || '',
          quantity: qty,
          unit_price: unit,
          total_price: tot,
          unit: item.unit || 'ks',
          description: item.description || '',
          taxRate: toMoneyNumber(item.taxRate ?? document.tax_rate ?? 0),
        });
      }
    });
  }
  
  // If still no items, try raw_data
  if (itemsMap.size === 0) {
    (() => {
      // Try to extract from raw_data - check multiple possible structures
      const rawData = document.raw_data || {};
      const invoice = rawData.Invoice || rawData.invoice || {};
      const quote = rawData.Quote || rawData.quote || {};
      
      // Try various item locations
      let rawItems = invoice.InvoiceItem || invoice.invoiceItem || 
                     quote.QuoteItem || quote.quoteItem || 
                     invoice.items || quote.items ||
                     rawData.items || rawData.Items ||
                     rawData.invoice_items || rawData.InvoiceItems ||
                     rawData.quote_items || rawData.QuoteItems || [];
      
      // If items is an object with numeric keys, convert to array
      if (typeof rawItems === 'object' && !Array.isArray(rawItems) && rawItems !== null) {
        rawItems = Object.values(rawItems);
      }
      
      if (Array.isArray(rawItems) && rawItems.length > 0) {
        rawItems.forEach((item: any, idx: number) => {
          const name = item.name || item.Name || item.product_name || item.ProductName || item.item_name || item.ItemName || '';
          const quantity = toMoneyNumber(
            item.quantity ?? item.Quantity ?? item.amount ?? item.Amount ?? item.qty ?? item.Qty ?? 0
          );
          const unitPrice = toMoneyNumber(
            item.unit_price ?? item.UnitPrice ?? item.price ?? item.Price ?? item.unitPrice ?? 0
          );
          const totalPrice = toMoneyNumber(
            item.total_price ?? item.TotalPrice ?? item.total ?? item.Total ?? item.totalPrice ?? 0
          );
          const unit = item.unit || item.Unit || item.unit_name || item.UnitName || 'ks';
          const description = item.description || item.Description || item.note || item.Note || item.comment || item.Comment || '';

          const key = `${name}_${quantity}_${unitPrice}`;
          if (!itemsMap.has(key)) {
            itemsMap.set(key, {
              id: `item-${idx}`,
              item_name: name,
              quantity,
              unit_price: unitPrice,
              total_price: totalPrice,
              unit,
              description,
              taxRate: toMoneyNumber(item.tax ?? item.Tax ?? item.tax_rate ?? item.taxRate ?? document.tax_rate ?? 0),
            });
          }
        });
      }
    })();
  }
  
  // Convert map to array
  const items = Array.from(itemsMap.values());

  // Extract supplier data from raw_data or extracted_data
  const supplierData = document.raw_data?.MyData || document.extracted_data?.supplier || {};
  const supplierName = supplierData.company_name || supplierData.name || "TPT Brány s.r.o.";
  const supplierAddress = supplierData.address || "";
  const supplierCity = supplierData.city || "";
  const supplierZip = supplierData.zip || "";
  // Handle country as string or object
  const supplierCountryRaw = supplierData.country || "Slovensko";
  const supplierCountry = typeof supplierCountryRaw === 'string' 
    ? supplierCountryRaw 
    : (supplierCountryRaw?.name || supplierCountryRaw?.iso || "Slovensko");
  const supplierIco = supplierData.ico || "";
  const supplierDic = supplierData.dic || "";
  const supplierIcDph = supplierData.ic_dph || "";
  const supplierIban = document.raw_data?.Invoice?.iban || document.raw_data?.iban || "";
  const supplierSwift = document.raw_data?.Invoice?.swift || document.raw_data?.swift || "";
  const supplierBankAccount = document.raw_data?.Invoice?.bank_account || document.raw_data?.bank_account || "";

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto p-6" showClose={true}>
        <DialogHeader className="mb-6 pb-4 border-b">
          {/* Compact Header - Title, Badge, and Dates in one line */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <DialogTitle className="text-2xl font-bold">
                {document.document_name || document.document_number || "Dokumentum"}
              </DialogTitle>
              <Badge variant="secondary" className="text-xs">{typeLabel}</Badge>
            </div>
            <DialogDescription className="sr-only">
              {document.document_name || document.document_number || "Dokumentum"} részletei
            </DialogDescription>
            <div className="flex items-center gap-4 text-xs">
              {document.document_date && (
                <div>
                  <span className="font-medium">{t("documentDetails.dateIssued")}:</span> {formatDate(document.document_date)}
                </div>
              )}
              {document.delivery_date && (
                <div>
                  <span className="font-medium">{t("documentDetails.dateDelivery")}:</span> {formatDate(document.delivery_date)}
                </div>
              )}
              {document.due_date && (
                <div>
                  <span className="font-medium">Dátum splatnosti:</span> {formatDate(document.due_date)}
                </div>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Two-column layout: Supplier (left) + Customer (right) */}
          <div className="grid grid-cols-2 gap-8">
            {/* Left Column - Supplier (DODÁVATEĽ) */}
            <div>
              <h3 className="font-bold text-base mb-3 uppercase">DODÁVATEĽ</h3>
              <div className="space-y-1.5 text-sm leading-relaxed">
                <div className="font-semibold text-base">{supplierName}</div>
                {(supplierAddress || supplierCity || supplierZip || supplierCountry) && (
                  <div className="space-y-0.5">
                    {supplierAddress && <div>{supplierAddress}</div>}
                    {(supplierCity || supplierZip) && (
                      <div>
                        {supplierZip && <span>{supplierZip}</span>}
                        {supplierCity && <span>{supplierZip ? " " : ""}{supplierCity}</span>}
                      </div>
                    )}
                    {supplierCountry && <div>{supplierCountry}</div>}
                  </div>
                )}
                {supplierIco && (
                  <div className="mt-2">
                    <span className="font-semibold">IČO:</span> {supplierIco}
                  </div>
                )}
                {supplierDic && (
                  <div>
                    <span className="font-semibold">DIČ:</span> {supplierDic}
                  </div>
                )}
                {supplierIcDph && (
                  <div>
                    <span className="font-semibold">IČ DPH:</span> {supplierIcDph}
                  </div>
                )}
                {(supplierBankAccount || supplierIban || supplierSwift) && (
                  <div className="mt-4 space-y-1">
                    {supplierBankAccount && (
                      <div>
                        <span className="font-semibold">Slovenská Sporiteľňa:</span> {supplierBankAccount}
                      </div>
                    )}
                    {(supplierIban || supplierSwift) && (
                      <div>
                        <span className="font-semibold">IBAN / SWIFT:</span> {supplierIban || ''} {supplierSwift ? `/ ${supplierSwift}` : ''}
                      </div>
                    )}
                  </div>
                )}
                {document.variable_symbol && (
                  <div className="mt-2">
                    <span className="font-semibold">{t("documentDetails.variableSymbol")}:</span> <span className="font-mono">{document.variable_symbol}</span>
                  </div>
                )}
                {document.payment_method && (
                  <div>
                    <span className="font-semibold">{t("documentDetails.paymentMethod")}:</span> {document.payment_method}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Customer (ODBERATEĽ) */}
            {(document.customer_name || document.customer_email || document.customer_address) && (
              <div>
                <h3 className="font-bold text-base mb-3 uppercase">ODBERATEĽ</h3>
                <div className="space-y-1.5 text-sm leading-relaxed">
                  {document.customer_name && (
                    <div className="font-semibold text-base">{document.customer_name}</div>
                  )}
                  {(document.customer_address || document.customer_city || document.customer_postal_code || document.customer_country) && (
                    <div className="space-y-0.5">
                      {document.customer_address && <div>{document.customer_address}</div>}
                      {(document.customer_city || document.customer_postal_code) && (
                        <div>
                          {document.customer_postal_code && <span>{document.customer_postal_code}</span>}
                          {document.customer_city && <span>{document.customer_postal_code ? " " : ""}{document.customer_city}</span>}
                        </div>
                      )}
                      {document.customer_country && (
                        <div>
                          {typeof document.customer_country === 'string' 
                            ? document.customer_country 
                            : (document.customer_country?.name || document.customer_country?.iso || '')}
                        </div>
                      )}
                    </div>
                  )}
                  {document.customer_ico && (
                    <div className="mt-2">
                      <span className="font-semibold">IČO:</span> {document.customer_ico}
                    </div>
                  )}
                  {document.customer_dic && (
                    <div>
                      <span className="font-semibold">DIČ:</span> {document.customer_dic}
                    </div>
                  )}
                  {(document.customer_ic_dph ?? (document as any).extracted_data?.customer?.icDph) && (
                    <div>
                      <span className="font-semibold">IČ DPH:</span> {document.customer_ic_dph ?? (document as any).extracted_data?.customer?.icDph}
                    </div>
                  )}
                  {document.customer_email && (
                    <div>
                      <span className="font-semibold">{t("documentDetails.emailLabel")}:</span> {document.customer_email}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div>
            <h3 className="font-bold text-base mb-3 uppercase">NÁZOV A POPIS POLOŽKY</h3>
            {items.length > 0 ? (
              <div className="border-2 border-border rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/70 border-b-2 border-border">
                    <tr>
                      <th className="px-4 py-3 text-left font-bold">NÁZOV A POPIS POLOŽKY</th>
                      <th className="px-4 py-3 text-right font-bold">MNOŽSTVO</th>
                      <th className="px-4 py-3 text-right font-bold">{t("documentDetails.unitPriceNoVat")}</th>
                      <th className="px-4 py-3 text-right font-bold">{t("documentDetails.vat")}</th>
                      <th className="px-4 py-3 text-right font-bold">{t("documentDetails.unitPriceWithVat")}</th>
                      <th className="px-4 py-3 text-right font-bold">{t("documentDetails.totalWithVat")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const unitPriceWithoutTax = toMoneyNumber(item.unit_price);
                      const qtyRaw = toMoneyNumber(item.quantity);
                      const quantity = qtyRaw > 0 ? qtyRaw : 1;
                      const documentHasNoVat = documentNotesIndicateNoVat(document.notes);
                      const taxRate = documentHasNoVat ? 0 : toMoneyNumber(item.taxRate ?? document.tax_rate ?? 0);
                      const { totalWithVat: totalPrice, unitPriceWithVat } = computeLineWithVatAmounts(
                        unitPriceWithoutTax,
                        quantity,
                        item.total_price,
                        taxRate
                      );

                      return (
                        <tr key={item.id} className="border-t border-border">
                          <td className="px-4 py-3 align-top">
                            <div className="font-medium">{item.item_name}</div>
                            {item.description && (
                              <div className="text-xs text-foreground/70 mt-1 leading-relaxed">
                                {item.description}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            {quantity} {item.unit || 'ks'}
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            {formatAmount(unitPriceWithoutTax, document.currency)}
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            {taxRate.toFixed(0)}%
                          </td>
                          <td className="px-4 py-3 text-right align-top">
                            {formatAmountUnitWithVat(unitPriceWithVat, document.currency, quantity)}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold align-top">
                            {formatAmount(totalPrice, document.currency)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="border-2 border-border rounded p-8 text-center text-foreground/60">
                <p className="text-sm">{t("documents.noItems")}</p>
              </div>
            )}
          </div>

          {/* Financial summary: Suma pred zľavou, Zľava X%, Suma bez DPH, DPH, Celková suma */}
          {(() => {
            const fin = document.extracted_data?.financial;
            const documentHasNoVat = documentNotesIndicateNoVat(document.notes);
            const effectiveTaxRate = documentHasNoVat ? 0 : toMoneyNumber(document.tax_rate ?? fin?.taxRate ?? 0);
            const hasNetDiscountLine = items.some((i) => toMoneyNumber(i.unit_price) < 0);

            const renderSummaryTable = (rows: { label: string; value: number; negative?: boolean }[]) => (
              <div className="flex justify-end mt-4">
                <div className="w-full max-w-md">
                  <div className="border border-border rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <tbody>
                        {rows.map((row) => (
                          <tr key={row.label} className="border-t border-border first:border-t-0">
                            <td className="px-3 py-2 font-medium">{row.label}</td>
                            <td className="px-3 py-2 text-right font-semibold">
                              {row.negative && row.value > 0 ? "−" : ""}
                              {formatAmount(row.value, document.currency ?? "EUR")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {document.related_document_number && (
                    <div className="text-xs text-foreground/70 italic mt-2 text-right">
                      Na základe {document.related_document_type === "proforma" ? "zálohovej faktúry" : "dokumentu"} {document.related_document_number}
                    </div>
                  )}
                </div>
              </div>
            );

            // Zľava as negative net line (Superfaktura estimate): aggregate from lines; skip document-level % rows
            if (hasNetDiscountLine && items.length > 0) {
              let netAgg = 0;
              let grossAgg = 0;
              for (const i of items) {
                const qtyRaw = toMoneyNumber(i.quantity);
                const q = qtyRaw > 0 ? qtyRaw : 1;
                const u = toMoneyNumber(i.unit_price);
                const tr = documentHasNoVat ? 0 : toMoneyNumber(i.taxRate ?? document.tax_rate ?? 0);
                const { totalWithVat } = computeLineWithVatAmounts(u, q, i.total_price, tr);
                netAgg += roundMoney(u * q);
                grossAgg += totalWithVat;
              }
              netAgg = roundMoney(netAgg);
              grossAgg = roundMoney(grossAgg);
              const totalSum = grossAgg;
              const sumWithoutVat = netAgg;
              const vatAmount = roundMoney(grossAgg - netAgg);
              if (!(Number.isFinite(totalSum) && (totalSum !== 0 || netAgg !== 0))) return null;

              const rows: { label: string; value: number; negative?: boolean }[] = [];
              if (Number.isFinite(sumWithoutVat)) {
                rows.push({ label: "Suma bez DPH", value: sumWithoutVat });
              }
              if (effectiveTaxRate > 0 && Number.isFinite(vatAmount)) {
                rows.push({ label: `DPH ${effectiveTaxRate}%`, value: vatAmount });
              }
              rows.push({ label: "Celková suma", value: totalSum });
              return renderSummaryTable(rows);
            }

            let totalSum = document.total_amount ?? fin?.total ?? 0;
            const itemsSumWithVat =
              items.length > 0 ? items.reduce((s, i) => s + toMoneyNumber(i.total_price), 0) : 0;
            let sumBeforeDiscount = fin?.sumBeforeDiscount;
            if (sumBeforeDiscount == null || sumBeforeDiscount <= 0) {
              sumBeforeDiscount = itemsSumWithVat > 0 ? itemsSumWithVat : totalSum;
            }
            let discountPercent = fin?.discountPercent ?? 0;
            let discountAmount = fin?.discountAmount ?? 0;
            if (sumBeforeDiscount > 0 && discountPercent > 0) {
              const correctDiscount = Math.round((sumBeforeDiscount * discountPercent / 100) * 100) / 100;
              if (discountAmount <= 0 || discountAmount >= sumBeforeDiscount * 0.99) {
                discountAmount = correctDiscount;
              }
            }
            if (sumBeforeDiscount > 0 && totalSum > 0 && totalSum < sumBeforeDiscount && discountAmount <= 0) {
              discountAmount = Math.round((sumBeforeDiscount - totalSum) * 100) / 100;
              if (discountPercent === 0) discountPercent = Math.round((discountAmount / sumBeforeDiscount) * 100);
            }
            if (discountPercent > 0 && sumBeforeDiscount > 0) {
              totalSum = Math.round((sumBeforeDiscount - discountAmount) * 100) / 100;
            }
            let sumWithoutVat = document.subtotal_amount ?? fin?.subtotal;
            let vatAmount = document.tax_amount ?? fin?.tax;
            if (effectiveTaxRate > 0 && totalSum > 0) {
              const derivedSumWithoutVat = totalSum / (1 + effectiveTaxRate / 100);
              const derivedVat = totalSum - derivedSumWithoutVat;
              if (sumWithoutVat == null || Math.abs(sumWithoutVat - totalSum) < 0.01) {
                sumWithoutVat = derivedSumWithoutVat;
                vatAmount = derivedVat;
              }
              if (vatAmount == null || vatAmount < 0) vatAmount = derivedVat;
            } else if (sumWithoutVat == null && totalSum > 0) {
              sumWithoutVat = totalSum;
            }
            const showSummary = totalSum > 0 || sumBeforeDiscount > 0;
            if (!showSummary) return null;

            const rows: { label: string; value: number; negative?: boolean }[] = [];
            if (discountPercent > 0 && sumBeforeDiscount > 0) {
              rows.push({ label: "Suma pred zľavou", value: sumBeforeDiscount });
              rows.push({ label: `Zľava ${discountPercent}%`, value: discountAmount, negative: true });
            }
            if (sumWithoutVat != null && sumWithoutVat >= 0) {
              rows.push({ label: "Suma bez DPH", value: sumWithoutVat });
            }
            if (effectiveTaxRate > 0 && vatAmount != null && vatAmount >= 0) {
              rows.push({ label: `DPH ${effectiveTaxRate}%`, value: vatAmount });
            }
            rows.push({ label: "Celková suma", value: totalSum });

            return renderSummaryTable(rows);
          })()}

          {/* Notes Section */}
          {document.notes && (
            <div>
              <h3 className="font-bold text-base mb-3">{t("documentDetails.notesLabel")}:</h3>
              <div className="p-6 border-2 border-border rounded-lg bg-muted/20 min-h-[150px]">
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{document.notes}</p>
              </div>
            </div>
          )}

          {/* PDF Link */}
          {document.pdf_url && (
            <div className="flex justify-end pt-4 border-t border-border">
              <a
                href={document.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                PDF megnyitása
              </a>
            </div>
          )}

        </div>
      </DialogContent>
    </Dialog>
  );
}

